import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FlexGrid, Radio, Label, Checkbox } from '@gasbuddy/react-components';

import Modal from '../modal';
import EmailChipList from '../email-chip-list';
import Notification from '../notification';
import StationListAccess from '../station-list-access';
import BpUtils from '../../../lib/bputils';
import { Roles, StationListType } from '../../../lib/constants/enums';

import styles from './styles.css';

const UserProps = PropTypes.shape({
  id: PropTypes.string,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
  email: PropTypes.string,
  title: PropTypes.string,
  department: PropTypes.string,
  enabled: PropTypes.bool,
  role_id: PropTypes.number,
  roles: PropTypes.arrayOf(PropTypes.string),
  station_lists: PropTypes.arrayOf(PropTypes.number),
  email_preferences: PropTypes.shape({
    dirty_broken_dangerous_report: PropTypes.bool,
    marketing_report: PropTypes.bool,
    performance_report: PropTypes.bool,
  }),
  permissions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    display_name: PropTypes.string,
    value: PropTypes.bool,
  })),
});

const InvitationProps = PropTypes.shape({
  invitation_email: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  sent_on: PropTypes.string,
  station_lists: PropTypes.arrayOf(PropTypes.number),
  user_permissions: PropTypes.shape({
    permissions: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.bool,
    })),
  }),
});

export default function UserInvitationModal({ user, show, onClose, onSave, onDeleteUser, onDeleteInvitation, stationLists }) {
  const [emails, setEmails] = React.useState([]);
  const [role, setRole] = React.useState(user?.roles ? user.roles[0] : Roles.Administrator);
  // eslint-disable-next-line no-nested-ternary
  const [reviewReplyAccess, setReviewReplyAccess] = React.useState(user
    ? (user?.id ? user.permissions.find(p => p.name === 'review_reply_free_form_text').value
      : user.user_properties.permissions.find(p => p.name === 'review_reply_free_form_text').value)
    : false);
  // eslint-disable-next-line no-nested-ternary
  const [updateGasPrices, setUpdateGasPrices] = React.useState(user
    ? (user?.id ? user.permissions.find(p => p.name === 'can_edit_gas_prices').value
      : user.user_properties.permissions.find(p => p.name === 'can_edit_gas_prices').value)
    : false);
  const [selectedStationLists, setSelectedStationLists] = React.useState(user?.station_lists || []);
  const [emailsError, setEmailsError] = React.useState('');

  React.useEffect(() => {
    if (user) {
      if (user?.id) {
        setRole(user.roles[0]);
        setReviewReplyAccess(user.permissions.find(p => p.name === 'review_reply_free_form_text').value);
        setUpdateGasPrices(user.permissions.find(p => p.name === 'can_edit_gas_prices').value);
      } else {
        // set invitation stuff
        setRole(user.roles[0]);
        setReviewReplyAccess(user.user_properties.permissions.find(p => p.name === 'review_reply_free_form_text').value);
        setUpdateGasPrices(user.user_properties.permissions.find(p => p.name === 'can_edit_gas_prices').value);
      }
      setSelectedStationLists(user.station_lists);
    }
  }, [user]);

  const resetModalControls = () => {
    setEmails([]);
    setRole(Roles.Administrator);
    setReviewReplyAccess(false);
    setUpdateGasPrices(false);
    setSelectedStationLists([]);
    setEmailsError('');
  };

  const handleOnClose = () => {
    resetModalControls();
    onClose();
  };

  const getPermissions = () => {
    const isAdmin = role === Roles.Administrator;
    const isEditor = role === Roles.Editor;
    return [
      { name: 'review_reply_free_form_text', value: isAdmin ? true : reviewReplyAccess },
      { name: 'can_edit_gas_prices', value: (isAdmin || isEditor) ? true : updateGasPrices },
    ];
  };

  const getSelectedStationLists = () => (role === Roles.Administrator
    ? stationLists.filter(sl => !(sl.list_type === StationListType.AllStations || sl.list_type === StationListType.Watched)).map(ssl => ssl.id)
    : selectedStationLists);

  const sendInvitation = () => {
    if (!emails.length) {
      setEmailsError('Please enter an email');
      return false;
    }
    BpUtils.api.post({
      path: '/users/invitations',
      data: {
        emails,
        roles: [role],
        station_lists: getSelectedStationLists(),
        user_properties: {
          permissions: getPermissions(),
        },
      },
      success: () => {
        Notification.success('The invitation was sent successfully');
        onSave();
      },
      error: () => Notification.error('The invitation was not sent successfully. Please try again.'),
    });
    return true;
  };

  const updateInvitation = () => {
    BpUtils.api.post({
      path: '/users/invitations',
      data: {
        emails: [user.invitation_email],
        roles: [role],
        station_lists: getSelectedStationLists(),
        user_properties: {
          permissions: getPermissions(),
        },
      },
      error: () => Notification.error('The invitation was not sent successfully. Please try again.'),
      success: () => {
        Notification.success('The invitation was resent successfully');
        onSave();
      },
    });
  };

  const updateUser = () => {
    BpUtils.api.put({
      path: `/users/${user.id}`,
      data: {
        roles: [role],
        station_lists: getSelectedStationLists(),
        permissions: getPermissions(),
      },
      error: () => {
        Notification.error('Something went wrong trying to update the user. Please try again.');
      },
      success: () => {
        Notification.success('The user was updated succesfully');
        onSave();
      },
    });
  };

  const handleOnSave = () => {
    if (!user) {
      return sendInvitation();
    } if (user?.id) {
      return updateUser();
    }
    return updateInvitation();
  };

  const deleteUser = async () => {
    await BpUtils.api.delete({
      path: `/users/${user.id}`,
      error: () => {
        Notification.error('Unable to delete the user');
      },
      success: () => {
        Notification.success('User deleted successfully');
        onDeleteUser(user);
      },
    });
  };

  const deleteInvitation = async () => {
    await BpUtils.api.delete({
      path: `/users/invitations/${user.invitation_email}`,
      contentType: 'application/json',
      error: () => {
        Notification.error('Unable to delete invitation');
      },
      success: () => {
        Notification.success('Invitation deleted successfully');
        onDeleteInvitation(user);
      },
    });
  };

  const handleOnDelete = async () => {
    if (user?.id) {
      deleteUser();
    } else {
      deleteInvitation();
    }
  };

  const handelOnUpdateGasPriceChange = () => {
    setUpdateGasPrices(!updateGasPrices);
  };

  const handleOnStationListAccessChange = (lists) => {
    setSelectedStationLists(lists.map(l => l.id));
  };

  return (
    <Modal
      // eslint-disable-next-line no-nested-ternary
      title={!user ? 'Invite New Users' : user?.id ? 'Edit User' : 'Edit Invitation'}
      show={show}
      onClose={handleOnClose}
      // eslint-disable-next-line no-nested-ternary
      saveButtonText={!user ? 'Invite' : user?.id ? 'Save' : 'Resend Invite'}
      onSave={handleOnSave}
      deleteButtonText={user ? 'Remove' : null}
      deleteConfirmationMessage={user ? 'Are you sure you want to delete this?' : null}
      onDelete={handleOnDelete}
    >
      <div>
        {!user ? (
          <EmailChipList
            recipients={emails}
            onListChange={setEmails}
            error={emailsError}
            onErrorChange={setEmailsError}
          />
        ) : null}
        { user && user?.id ? (
          <>
            <h5>{`${user.first_name} ${user.last_name}`}</h5>
            <div>{user.title}</div>
            <div>{user.email}</div>
          </>
        ) : null}
        { user && !user?.id ? (
          <>
            <h5>{user.invitation_email}</h5>
            <div>{`Invited on ${moment(user.sent_on).format('MMM Do, YYYY')}`}</div>
          </>
        ) : null}
        <br />
        <StationListAccess
          stationLists={stationLists}
          onChange={handleOnStationListAccessChange}
          disabled={role === Roles.Administrator}
          selectedListIds={selectedStationLists}
        />
        {user && user?.id ? (
          <>
          </>
        ) : null}
        <hr className={styles.dottedBorder} />
        <div>
          <div><h4>User Type</h4></div>
          <FlexGrid container>
            <FlexGrid.Column mobile={6}>
              <Label htmlFor="adminRole" className={styles.radioLabel}>
                <div className={styles.checkboxContainer}>
                  <Radio
                    id="adminRole"
                    value="administrator"
                    name="role"
                    checked={role === Roles.Administrator}
                    onClick={() => setRole(Roles.Administrator)}
                  />
                </div>
                <div className={styles.checkboxInfo}>
                  <div className={styles.checkboxName}>Admin</div>
                  <div className={styles.checkboxDescription}>
                    User has full access to all dashboard features and information, and can manage other users.
                  </div>
                </div>
              </Label>
            </FlexGrid.Column>
            <FlexGrid.Column mobile={6}>
              <Label htmlFor="editorRole" className={styles.radioLabel}>
                <div className={styles.checkboxContainer}>
                  <Radio
                    id="editorRole"
                    value="editor"
                    name="role"
                    checked={role === Roles.Editor}
                    onClick={() => setRole(Roles.Editor)}
                  />
                </div>
                <div className={styles.checkboxInfo}>
                  <div className={styles.checkboxName}>Editor</div>
                  <div className={styles.checkboxDescription}>
                    User can only edit or create private station lists. They can export data and create/edit items in the dashboard.
                  </div>
                </div>
              </Label>
            </FlexGrid.Column>
            <FlexGrid.Column mobile={6}>
              <Label htmlFor="observerRole" className={styles.radioLabel}>
                <div className={styles.checkboxContainer}>
                  <Radio
                    id="observerRole"
                    value="observer"
                    name="role"
                    checked={role === Roles.Observer}
                    onClick={() => setRole(Roles.Observer)}
                  />
                </div>
                <div className={styles.checkboxInfo}>
                  <div className={styles.checkboxName}>Observer</div>
                  <div className={styles.checkboxDescription}>
                    User can only view. They cannot create lists, export data or add to the dashboard.
                  </div>
                </div>
              </Label>
            </FlexGrid.Column>
            <FlexGrid.Column mobile={6}>
              <Label htmlFor="fuelShortageRole" className={styles.radioLabel}>
                <div className={styles.checkboxContainer}>
                  <Radio
                    id="fuelShortageRole"
                    value="fuel_shortage"
                    name="role"
                    checked={role === Roles.Fuel_Shortage}
                    onClick={() => setRole(Roles.Fuel_Shortage)}
                  />
                </div>
                <div className={styles.checkboxInfo}>
                  <div className={styles.checkboxName}>Fuel Shortage</div>
                  <div className={styles.checkboxDescription}>
                    User can update station info when the Outage Tracker is enabled.
                  </div>
                </div>
              </Label>
            </FlexGrid.Column>
          </FlexGrid>
        </div>
        {role === Roles.Editor || role === Roles.Observer ? (
          <>
            <hr className={styles.dottedBorder} />
            <div><h4>User Access</h4></div>
          </>
        ) : null}
        {role === Roles.Editor ? (
          <>
            <div>
              <div><h5>Review replies</h5></div>
              <FlexGrid container>
                <FlexGrid.Column mobile={6}>
                  <Label htmlFor="reviewReplyFreeform" className={styles.radioLabel}>
                    <div className={styles.checkboxContainer}>
                      <Radio
                        id="reviewReplyFreeform"
                        value="review_reply_free_form_text"
                        name="reviewReply"
                        checked={reviewReplyAccess}
                        onClick={() => setReviewReplyAccess(true)}
                      />
                    </div>
                    <div className={styles.checkboxInfo}>
                      <div className={styles.checkboxName}>Free-form text</div>
                    </div>
                  </Label>
                </FlexGrid.Column>
                <FlexGrid.Column mobile={6}>
                  <Label htmlFor="reviewReplyTemplateOnly" className={styles.radioLabel}>
                    <div className={styles.checkboxContainer}>
                      <Radio
                        id="reviewReplyTemplateOnly"
                        value="review_reply_free_form_text"
                        name="reviewReply"
                        checked={!reviewReplyAccess}
                        onClick={() => setReviewReplyAccess(false)}
                      />
                    </div>
                    <div className={styles.checkboxInfo}>
                      <div className={styles.checkboxName}>Template only</div>
                    </div>
                  </Label>
                </FlexGrid.Column>
              </FlexGrid>
            </div>
          </>
        ) : null}
        {role === Roles.Observer ? (
          <div>
            <div><h5>Gas Prices</h5></div>
            <FlexGrid container>
              <FlexGrid.Column mobile={6}>
                <Label htmlFor="updateGasPrices" className={styles.radioLabel}>
                  <div className={styles.checkboxContainer}>
                    <Checkbox id="updateGasPrices" checked={updateGasPrices} onChange={handelOnUpdateGasPriceChange} />
                  </div>
                  <div className={styles.checkboxInfo}>
                    <div className={styles.checkboxName}>Update Gas Prices</div>
                  </div>
                </Label>
              </FlexGrid.Column>
            </FlexGrid>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

UserInvitationModal.propTypes = {
  user: PropTypes.oneOfType([UserProps, InvitationProps]),
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onDeleteUser: PropTypes.func,
  onDeleteInvitation: PropTypes.func,
  stationLists: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    list_type: PropTypes.string,
    station_count: PropTypes.number,
  })),
};

UserInvitationModal.defaultProps = {
  user: null,
  show: false,
  onClose: () => { },
  onSave: () => { },
  onDeleteUser: () => { },
  onDeleteInvitation: () => { },
  stationLists: [],
};
